import React, { useEffect, useRef, useState } from "react";
import { useScrollLock } from "../hooks/useScrollLock";
import {
  MessageCircle, Send, Bell, X, Ticket, CheckCircle,
  Headphones, Phone, Save,
  Award, Star, DollarSign, AlertCircle, MapPin, Gift,
  Zap, Flag, TrendingUp, Users, Info, Clock,
  Paperclip, Image, Mic, Video,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useSocket } from "../context/SocketContext";
import {
  getSettings, updateSetting, getEmployees, uploadChatFile,
  getNotificationHistory, getNotificationTemplates,
  createNotificationTemplate, deleteNotificationTemplate,
} from "../services/api";

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatTime(d) {
  return new Date(d).toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit" });
}
function formatDay(d) {
  return new Date(d).toLocaleDateString("es-AR", { day: "2-digit", month: "short", year: "numeric" });
}
function getInitials(name = "") {
  return name.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function SoportePage({ embedded = false }) {
  const { user } = useAuth();
  const [mainTab, setMainTab] = useState("chat"); // "chat" | "config"

  return (
    <div style={{ ...s.page, ...(embedded ? { height: "100%", flex: 1 } : {}) }}>
      {!embedded && (
        <div style={s.pageHeader}>
          <div>
            <h1 style={s.pageTitle}>Soporte</h1>
            <p style={s.pageSubtitle}>Chat en vivo y configuración del número de contacto</p>
          </div>
        </div>
      )}

      {/* Main tabs */}
      <div style={s.mainTabs}>
        <button
          style={{ ...s.mainTab, ...(mainTab === "chat" ? s.mainTabActive : {}) }}
          onClick={() => setMainTab("chat")}
        >
          <MessageCircle size={15} />
          Chat en vivo
        </button>
        <button
          style={{ ...s.mainTab, ...(mainTab === "config" ? s.mainTabActive : {}) }}
          onClick={() => setMainTab("config")}
        >
          <Headphones size={15} />
          Número de contacto
        </button>
      </div>

      {mainTab === "chat"   && <ChatSection user={user} />}
      {mainTab === "config" && <ConfigSection user={user} />}
    </div>
  );
}

// ── Chat section (full ChatPage content) ─────────────────────────────────────

function ChatSection() {
  const { socket, setChatUnread } = useSocket();
  const [conversations, setConversations] = useState([]);
  const [selected, setSelected]           = useState(null);
  const [messages, setMessages]           = useState([]);
  const [input, setInput]                 = useState("");
  const [connected, setConnected]         = useState(false);
  const selectedRef   = useRef(null);
  const messagesEndRef = useRef(null);

  const [activeTab, setActiveTab]           = useState("convs");
  const activeTabRef                        = useRef("convs");
  const [tickets, setTickets]               = useState([]);
  const [archivedTickets, setArchivedTickets] = useState([]);
  const [showArchived, setShowArchived]     = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketMessages, setTicketMessages] = useState([]);
  const [closingChat, setClosingChat]       = useState(false);
  const [archiving, setArchiving]           = useState(false);
  const [closeModal, setCloseModal]         = useState(false);
  const [ticketName, setTicketName]         = useState("");

  const [uploading, setUploading]   = useState(false);
  const [attachMenu, setAttachMenu] = useState(false);
  const fileInputRef                = useRef(null);
  const fileAcceptRef               = useRef("");
  const [lightbox, setLightbox]     = useState(null);

  const [employees, setEmployees]   = useState([]);
  const [notifModal, setNotifModal] = useState(false);
  useScrollLock(closeModal || notifModal);
  const [notifText, setNotifText]   = useState("");
  const [notifTitle, setNotifTitle] = useState("");
  const [notifIcon, setNotifIcon]   = useState("");
  const [notifTarget, setNotifTarget] = useState("all");
  const [notifSent, setNotifSent]   = useState(false);
  const [notifTab, setNotifTab]     = useState("send"); // "send" | "history" | "templates"
  const [notifHistory, setNotifHistory]     = useState([]);
  const [notifTemplates, setNotifTemplates] = useState([]);
  const [newTemplateName, setNewTemplateName] = useState("");
  const [savingTemplate, setSavingTemplate]   = useState(false);

  const ICONS = [
    { name: "award",        Icon: Award,       label: "Premio"     },
    { name: "star",         Icon: Star,        label: "Destacado"  },
    { name: "dollar-sign",  Icon: DollarSign,  label: "Pago"       },
    { name: "bell",         Icon: Bell,        label: "Aviso"      },
    { name: "check-circle", Icon: CheckCircle, label: "Completado" },
    { name: "alert-circle", Icon: AlertCircle, label: "Atención"   },
    { name: "map-pin",      Icon: MapPin,      label: "Ruta"       },
    { name: "gift",         Icon: Gift,        label: "Regalo"     },
    { name: "zap",          Icon: Zap,         label: "Urgente"    },
    { name: "flag",         Icon: Flag,        label: "Indicador"  },
    { name: "trending-up",  Icon: TrendingUp,  label: "Progreso"   },
    { name: "users",        Icon: Users,       label: "Equipo"     },
    { name: "info",         Icon: Info,        label: "Info"       },
    { name: "clock",        Icon: Clock,       label: "Plazo"      },
  ];

  useEffect(() => { selectedRef.current  = selected;  }, [selected]);
  useEffect(() => { activeTabRef.current = activeTab; }, [activeTab]);
  useEffect(() => { getEmployees().then(setEmployees).catch(() => {}); }, []);

  useEffect(() => {
    if (!socket) return;
    setConnected(socket.connected);

    function onConnect() {
      setConnected(true);
      if (activeTabRef.current === "tickets") socket.emit("chat:tickets");
      else socket.emit("chat:conversations");
    }
    function onDisconnect()      { setConnected(false); }
    function onConversations(d)  { setConversations(d); }
    function onHistory(msgs)     { setMessages(msgs); }
    function onMessage(msg) {
      const sel = selectedRef.current;
      setMessages((prev) => {
        const belongs = sel?.id === msg.user_id || (prev.length > 0 && prev[0]?.user_id === msg.user_id);
        if (!belongs || prev.some((m) => m.id === msg.id)) return prev;
        return [...prev, msg];
      });
      socket.emit("chat:conversations");
    }
    function onUnreadUpdate()         { socket.emit("chat:conversations"); }
    function onTickets(d)             { setTickets(d); }
    function onArchivedTickets(d)     { setArchivedTickets(d); }
    function onArchiveDone()          { setArchiving(false); setSelectedTicket(null); setTicketMessages([]); }
    function onTicketHistory(msgs)    { setTicketMessages(msgs); }
    function onChatClosed()           { setClosingChat(false); setSelected(null); setMessages([]); socket.emit("chat:tickets"); setActiveTab("tickets"); }

    socket.on("connect",               onConnect);
    socket.on("disconnect",            onDisconnect);
    socket.on("chat:conversations",    onConversations);
    socket.on("chat:history",          onHistory);
    socket.on("chat:message",          onMessage);
    socket.on("chat:unread_update",    onUnreadUpdate);
    socket.on("chat:tickets",          onTickets);
    socket.on("chat:archived_tickets", onArchivedTickets);
    socket.on("chat:archive_done",     onArchiveDone);
    socket.on("chat:ticket_history",   onTicketHistory);
    socket.on("chat:closed",           onChatClosed);

    if (socket.connected) {
      setConnected(true);
      if (activeTabRef.current === "tickets") socket.emit("chat:tickets");
      else socket.emit("chat:conversations");
    } else {
      socket.connect();
    }

    return () => {
      socket.off("connect",               onConnect);
      socket.off("disconnect",            onDisconnect);
      socket.off("chat:conversations",    onConversations);
      socket.off("chat:history",          onHistory);
      socket.off("chat:message",          onMessage);
      socket.off("chat:unread_update",    onUnreadUpdate);
      socket.off("chat:tickets",          onTickets);
      socket.off("chat:archived_tickets", onArchivedTickets);
      socket.off("chat:archive_done",     onArchiveDone);
      socket.off("chat:ticket_history",   onTicketHistory);
      socket.off("chat:closed",           onChatClosed);
    };
  }, [socket]);

  // Cuando el usuario cambia de tab estando conectado, pedir los datos del tab nuevo
  useEffect(() => {
    if (!socket?.connected) return;
    if (activeTab === "tickets") socket.emit("chat:tickets");
    if (activeTab === "convs")   socket.emit("chat:conversations");
  }, [activeTab]);

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);
  useEffect(() => { setChatUnread(0); }, []);

  function selectConversation(conv) {
    setSelected(conv); setMessages([]);
    socket?.emit("chat:history", { targetUserId: conv.id });
    socket?.emit("chat:read", { targetUserId: conv.id });
    setConversations((prev) => prev.map((c) => c.id === conv.id ? { ...c, unread_count: 0 } : c));
  }
  function sendMessage() {
    const text = input.trim();
    if (!text || !selected || !socket?.connected) return;
    socket.emit("chat:send", { content: text, targetUserId: selected.id });
    setInput("");
  }
  function closeChat() { if (!selected || !socket?.connected) return; setTicketName(""); setCloseModal(true); }
  function confirmCloseChat() { if (!selected) return; setCloseModal(false); setClosingChat(true); socket.emit("chat:close", { targetUserId: selected.id, name: ticketName.trim() || null }); }
  function selectTicket(t) { setSelectedTicket(t); setSelected(null); setMessages([]); setTicketMessages([]); socket?.emit("chat:ticket_history", { ticketId: t.id }); }
  function switchTab(tab) {
    setActiveTab(tab);
    setSelected(null); setSelectedTicket(null); setMessages([]); setTicketMessages([]);
    setShowArchived(false);
  }
  function toggleArchived() {
    const next = !showArchived;
    setShowArchived(next);
    setSelectedTicket(null); setTicketMessages([]);
    if (next) socket?.emit("chat:archived_tickets");
    else      socket?.emit("chat:tickets");
  }
  function archiveTicket(t) {
    if (!socket?.connected) return;
    setArchiving(true);
    socket.emit("chat:archive_ticket", { ticketId: t.id });
  }
  function openAttachMenu(accept) {
    fileAcceptRef.current = accept;
    setAttachMenu(false);
    fileInputRef.current.accept = accept;
    fileInputRef.current.click();
  }
  async function handleFileSelected(e) {
    const file = e.target.files?.[0];
    if (!file || !selected || !socket?.connected) return;
    e.target.value = "";
    setUploading(true);
    try {
      const { url, file_type } = await uploadChatFile(file);
      socket.emit("chat:send", {
        content: "",
        targetUserId: selected.id,
        file_url: url,
        file_type,
      });
    } catch (err) {
      window.alert(err.message || "Error al subir archivo");
    } finally {
      setUploading(false);
    }
  }

  function openNotifModal() {
    setNotifModal(true);
    setNotifTab("send");
    getNotificationHistory().then(setNotifHistory).catch(() => {});
    getNotificationTemplates().then(setNotifTemplates).catch(() => {});
  }

  function sendNotification() {
    if (!notifText.trim() || !socket?.connected) return;
    socket.emit("notification:send", {
      title:        notifTitle.trim() || null,
      icon:         notifIcon         || null,
      content:      notifText.trim(),
      targetUserId: notifTarget === "all" ? null : notifTarget,
    });
    setNotifSent(true);
    setTimeout(() => {
      setNotifSent(false);
      setNotifModal(false);
      setNotifTarget("all");
      setNotifText(""); setNotifTitle(""); setNotifIcon("");
    }, 1800);
  }

  function resendNotification(h) {
    setNotifTitle(h.title || "");
    setNotifIcon(h.icon  || "");
    setNotifText(h.content);
    setNotifTarget(h.target_user_id || "all");
    setNotifTab("send");
  }

  function applyTemplate(t) {
    setNotifTitle(t.title || "");
    setNotifIcon(t.icon  || "");
    setNotifText(t.content);
    setNotifTab("send");
  }

  async function saveTemplate() {
    if (!notifText.trim() || !newTemplateName.trim()) return;
    setSavingTemplate(true);
    try {
      const t = await createNotificationTemplate({
        name: newTemplateName.trim(),
        title: notifTitle.trim() || null,
        icon: notifIcon || null,
        content: notifText.trim(),
      });
      setNotifTemplates((prev) => [t, ...prev]);
      setNewTemplateName("");
    } catch { /* silencioso */ }
    finally { setSavingTemplate(false); }
  }

  async function removeTemplate(id) {
    await deleteNotificationTemplate(id).catch(() => {});
    setNotifTemplates((prev) => prev.filter((t) => t.id !== id));
  }

  function renderMedia(msg) {
    if (!msg.file_url) return null;
    if (msg.file_type === "image") {
      return (
        <img
          src={msg.file_url}
          alt="imagen"
          style={s.mediaBubbleImg}
          onClick={() => setLightbox(msg.file_url)}
        />
      );
    }
    if (msg.file_type === "audio") {
      return <audio src={msg.file_url} controls style={s.mediaBubbleAudio} />;
    }
    if (msg.file_type === "video") {
      return <video src={msg.file_url} controls style={s.mediaBubbleVideo} />;
    }
    return null;
  }

  const totalUnread = conversations.reduce((sum, c) => sum + (c.unread_count || 0), 0);

  return (
    <>
    <div style={s.chatWrap}>
      {/* Subheader */}
      <div style={s.chatSubHeader}>
        <div style={s.connStatus}>
          <span style={{ ...s.connDot, backgroundColor: connected ? "#16a34a" : "#9a9a93" }} />
          <span style={s.connText}>{connected ? "Conectado" : "Desconectado"}</span>
          {!connected && (
            <button style={s.reconnectBtn} onClick={() => socket?.connect()}>
              Reconectar
            </button>
          )}
          {totalUnread > 0 && <span style={s.unreadBadge}>{totalUnread} sin leer</span>}
        </div>
        <button style={s.notifBtn} onClick={openNotifModal}>
          <Bell size={14} /> Enviar aviso
        </button>
      </div>

      <div style={s.layout}>
        {/* Sidebar */}
        <div style={s.sidebar}>
          <div style={s.tabs}>
            <button style={{ ...s.tabBtn, ...(activeTab === "convs"   ? s.tabBtnActive : {}) }} onClick={() => switchTab("convs")}>
              <MessageCircle size={13} /> Chats
              {totalUnread > 0 && <span style={s.convBadge}>{totalUnread}</span>}
            </button>
            <button style={{ ...s.tabBtn, ...(activeTab === "tickets" ? s.tabBtnActive : {}) }} onClick={() => switchTab("tickets")}>
              <Ticket size={13} /> Tickets
            </button>
          </div>

          {activeTab === "convs" && (() => {
            const convMap = new Map(conversations.map(c => [c.id, c]));
            const allUsers = employees.map(e => {
              const conv = convMap.get(e.id);
              return conv
                ? { ...conv, phone: e.phone }
                : { id: e.id, name: e.name, email: e.email, phone: e.phone, unread_count: 0, last_message: null };
            });
            if (allUsers.length === 0) return (
              <div style={s.sidebarEmpty}><MessageCircle size={28} color="#d1d5db" /><p style={{ margin: "8px 0 0", color: "var(--text-3)", fontSize: 13 }}>Sin usuarios</p></div>
            );
            const sorted = [...allUsers].sort((a, b) => {
              const aUnread = a.unread_count || 0;
              const bUnread = b.unread_count || 0;
              if (aUnread !== bUnread) return bUnread - aUnread;
              const aTime = a.last_message_at ? new Date(a.last_message_at).getTime() : (a.last_message ? 1 : 0);
              const bTime = b.last_message_at ? new Date(b.last_message_at).getTime() : (b.last_message ? 1 : 0);
              return bTime - aTime;
            });
            return sorted.map((c) => (
              <button key={c.id} style={{ ...s.convItem, ...(selected?.id === c.id ? s.convItemActive : {}) }} onClick={() => selectConversation(c)}>
                <div style={s.convAvatar}>{getInitials(c.name)}</div>
                <div style={s.convInfo}>
                  <div style={s.convName}>{c.name}</div>
                  <div style={s.convLast}>
                    {c.last_message ? (c.last_message.length > 35 ? c.last_message.slice(0, 35) + "…" : c.last_message) : <span style={{ color: "var(--text-3)", fontStyle: "italic" }}>Sin mensajes</span>}
                  </div>
                </div>
                {c.unread_count > 0 && <span style={s.convBadge}>{c.unread_count}</span>}
              </button>
            ));
          })()}

          {activeTab === "tickets" && (() => {
            const list = showArchived ? archivedTickets : tickets;
            return (
              <>
                {list.length === 0
                  ? <div style={s.sidebarEmpty}><Ticket size={28} color="#d1d5db" /><p style={{ margin: "8px 0 0", color: "var(--text-3)", fontSize: 13 }}>{showArchived ? "Sin tickets archivados" : "Sin tickets"}</p></div>
                  : list.map((t) => (
                    <button key={t.id} style={{ ...s.convItem, ...(selectedTicket?.id === t.id ? s.convItemActive : {}) }} onClick={() => selectTicket(t)}>
                      <div style={{ ...s.convAvatar, background: showArchived ? "#f0fdf4" : "#f1f5f9", color: showArchived ? "#16a34a" : "#64748b" }}>
                        <Ticket size={14} />
                      </div>
                      <div style={s.convInfo}>
                        <div style={s.convName}>{t.name || t.user_name || t.user_email}</div>
                        <div style={s.convLast}>#{t.id} · {t.user_name} · {new Date(t.closed_at).toLocaleDateString("es-AR")} · {t.msg_count} msgs</div>
                      </div>
                    </button>
                  ))
                }
                <button style={s.archivedToggleBtn} onClick={toggleArchived}>
                  {showArchived ? "← Ver tickets activos" : "📦 Ver archivados"}
                </button>
              </>
            );
          })()}
        </div>

        {/* Chat panel */}
        <div style={s.chatPanel}>
          {!selected && !selectedTicket ? (
            <div style={s.noSelection}>
              <MessageCircle size={48} color="#d1d5db" />
              <p style={s.noSelectionText}>{activeTab === "tickets" ? "Seleccioná un ticket para ver el historial" : "Seleccioná una conversación para comenzar"}</p>
            </div>

          ) : selectedTicket ? (
            <>
              <div style={s.chatHeader}>
                <div style={{ ...s.chatAvatar, background: "#f1f5f9", color: "#64748b" }}><Ticket size={16} /></div>
                <div style={{ flex: 1 }}>
                  <div style={s.chatHeaderName}>{selectedTicket.name || `Ticket #${selectedTicket.id}`}</div>
                  <div style={s.chatHeaderEmail}>{selectedTicket.user_name} · cerrado el {new Date(selectedTicket.closed_at).toLocaleDateString("es-AR", { day: "2-digit", month: "short", year: "numeric" })}</div>
                </div>
                {selectedTicket.archived
                  ? <div style={s.ticketArchivedBadge}>📦 Archivado</div>
                  : <div style={s.ticketClosedBadge}><CheckCircle size={13} /> Cerrado</div>
                }
                {!selectedTicket.archived && (
                  <button
                    style={{ ...s.archiveBtn, opacity: archiving ? 0.6 : 1 }}
                    onClick={() => archiveTicket(selectedTicket)}
                    disabled={archiving}
                    title="Archivar ticket — el usuario no lo verá más"
                  >
                    {archiving ? "..." : "📦 Archivar"}
                  </button>
                )}
              </div>
              <div style={s.messages}>
                <div style={s.ticketBanner}>🎫 Ticket #{selectedTicket.id} — {selectedTicket.msg_count} mensajes · {new Date(selectedTicket.created_at).toLocaleDateString("es-AR")} → {new Date(selectedTicket.closed_at).toLocaleDateString("es-AR")}</div>
                {ticketMessages.length === 0
                  ? <div style={s.messagesEmpty}><MessageCircle size={32} color="#d1d5db" /></div>
                  : (() => {
                      let lastDay = null;
                      return ticketMessages.map((msg) => {
                        const isAdmin = msg.sender_role === "admin";
                        const day = formatDay(msg.created_at); const showDay = day !== lastDay; lastDay = day;
                        return (
                          <React.Fragment key={msg.id}>
                            {showDay && <div style={s.dayDivider}><span style={s.dayLabel}>{day}</span></div>}
                            <div style={{ ...s.msgRow, justifyContent: isAdmin ? "flex-end" : "flex-start" }}>
                              {!isAdmin && <div style={s.userAvatar}>{getInitials(selectedTicket.user_name)}</div>}
                              <div style={{ ...s.bubble, ...(isAdmin ? s.bubbleAdmin : s.bubbleUser) }}>
                                {renderMedia(msg)}
                                {msg.content && <p style={s.bubbleText}>{msg.content}</p>}
                                <span style={s.bubbleTime}>{formatTime(msg.created_at)}</span>
                              </div>
                            </div>
                          </React.Fragment>
                        );
                      });
                    })()
                }
                <div ref={messagesEndRef} />
              </div>
            </>

          ) : (
            <>
              <div style={s.chatHeader}>
                <div style={s.chatAvatar}>{getInitials(selected.name)}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={s.chatHeaderName}>{selected.name}</div>
                  <div style={s.chatHeaderEmail}>{selected.email}</div>
                </div>
                {selected.phone && (
                  <a href={`tel:${selected.phone}`} style={s.chatHeaderPhone} title="Llamar">
                    <Phone size={14} />
                    {selected.phone}
                  </a>
                )}
                <button style={{ ...s.closeChatBtn, opacity: closingChat ? 0.6 : 1 }} onClick={closeChat} disabled={closingChat}>
                  {closingChat ? "Finalizando..." : "✓ Finalizar chat"}
                </button>
              </div>
              <div style={s.messages}>
                {messages.length === 0
                  ? <div style={s.messagesEmpty}><MessageCircle size={32} color="#d1d5db" /><p style={{ margin: "8px 0 0", color: "var(--text-3)", fontSize: 13 }}>Sin mensajes</p></div>
                  : (() => {
                      let lastDay = null;
                      return messages.map((msg) => {
                        const isAdmin = msg.sender_role === "admin";
                        const day = formatDay(msg.created_at); const showDay = day !== lastDay; lastDay = day;
                        return (
                          <React.Fragment key={msg.id}>
                            {showDay && <div style={s.dayDivider}><span style={s.dayLabel}>{day}</span></div>}
                            <div style={{ ...s.msgRow, justifyContent: isAdmin ? "flex-end" : "flex-start" }}>
                              {!isAdmin && <div style={s.userAvatar}>{getInitials(selected.name)}</div>}
                              <div style={{ ...s.bubble, ...(isAdmin ? s.bubbleAdmin : s.bubbleUser) }}>
                                {renderMedia(msg)}
                                {msg.content && <p style={s.bubbleText}>{msg.content}</p>}
                                <span style={s.bubbleTime}>{formatTime(msg.created_at)}{isAdmin && (msg.read_by_user ? " ✓✓" : " ✓")}</span>
                              </div>
                            </div>
                          </React.Fragment>
                        );
                      });
                    })()
                }
                <div ref={messagesEndRef} />
              </div>
              <div style={s.inputRow}>
                {/* Hidden file input */}
                <input
                  ref={fileInputRef}
                  type="file"
                  style={{ display: "none" }}
                  onChange={handleFileSelected}
                />
                {/* Attach button + menu */}
                <div style={{ position: "relative", flexShrink: 0 }}>
                  <button
                    style={{ ...s.attachBtn, opacity: (!connected || uploading) ? 0.5 : 1 }}
                    onClick={() => setAttachMenu((v) => !v)}
                    disabled={!connected || uploading}
                    title="Adjuntar archivo"
                  >
                    {uploading ? <span style={{ fontSize: 11, fontWeight: 700 }}>...</span> : <Paperclip size={16} />}
                  </button>
                  {attachMenu && (
                    <div style={s.attachMenu}>
                      <button style={s.attachMenuItem} onClick={() => openAttachMenu("image/*")}>
                        <Image size={14} color="#D4620D" /> Foto
                      </button>
                      <button style={s.attachMenuItem} onClick={() => openAttachMenu("audio/*")}>
                        <Mic size={14} color="#7c3aed" /> Audio
                      </button>
                      <button style={s.attachMenuItem} onClick={() => openAttachMenu("video/*")}>
                        <Video size={14} color="#0891b2" /> Video
                      </button>
                    </div>
                  )}
                </div>
                <textarea style={s.input} value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); } }} placeholder="Escribí un mensaje... (Enter para enviar)" rows={1} maxLength={1000} />
                <button style={{ ...s.sendBtn, ...(!input.trim() || !connected ? s.sendBtnDisabled : {}) }} onClick={sendMessage} disabled={!input.trim() || !connected}>
                  <Send size={18} />
                </button>
              </div>
              {/* Lightbox para imágenes del chat */}
              {lightbox && (
                <div style={s.lightboxOverlay} onClick={() => setLightbox(null)}>
                  <img src={lightbox} alt="media" style={s.lightboxImg} />
                  <button style={s.lightboxClose} onClick={() => setLightbox(null)}>✕</button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>

    {closeModal && (
      <div style={s.modalOverlay} onClick={() => setCloseModal(false)}>
        <div style={s.modal} onClick={(e) => e.stopPropagation()}>
          <div style={s.modalHeader}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ ...s.modalIcon, background: "#fee2e2" }}><CheckCircle size={18} color="#dc2626" /></div>
              <span style={s.modalTitle}>Finalizar chat</span>
            </div>
            <button style={s.modalClose} onClick={() => setCloseModal(false)}><X size={18} /></button>
          </div>
          <div style={s.modalBody}>
            <p style={{ fontSize: 13, color: "var(--text-2)", margin: "0 0 16px" }}>Se guardará como ticket vinculado a <strong>{selected?.name}</strong>.</p>
            <label style={s.modalLabel}>Nombre del ticket</label>
            <input style={{ ...s.modalSelect, marginBottom: 4 }} placeholder="Ej: Consulta sobre ruta Norte — Mayo 2026" value={ticketName} onChange={(e) => setTicketName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && confirmCloseChat()} autoFocus />
            <div style={{ fontSize: 11, color: "var(--text-3)", marginTop: 4 }}>Opcional</div>
          </div>
          <div style={s.modalFooter}>
            <button style={s.modalCancel} onClick={() => setCloseModal(false)}>Cancelar</button>
            <button style={{ ...s.modalSend, background: "#dc2626" }} onClick={confirmCloseChat}><CheckCircle size={14} /> Finalizar y guardar ticket</button>
          </div>
        </div>
      </div>
    )}

    {notifModal && (
      <div style={s.modalOverlay} onClick={() => setNotifModal(false)}>
        <div style={{ ...s.modal, maxWidth: 560 }} onClick={(e) => e.stopPropagation()}>
          <div style={s.modalHeader}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={s.modalIcon}><Bell size={18} color="#D4620D" /></div>
              <span style={s.modalTitle}>Avisos</span>
            </div>
            <button style={s.modalClose} onClick={() => setNotifModal(false)}><X size={18} /></button>
          </div>

          {/* Tabs */}
          <div style={{ display: "flex", borderBottom: "1px solid var(--border)", padding: "0 20px" }}>
            {[["send","Enviar"],["history","Historial"],["templates","Plantillas"]].map(([k, label]) => (
              <button key={k} onClick={() => setNotifTab(k)} style={{
                padding: "10px 16px", border: "none", background: "none", cursor: "pointer",
                fontWeight: 700, fontSize: 13, fontFamily: "Montserrat, sans-serif",
                color: notifTab === k ? "#D4620D" : "var(--text-2)",
                borderBottom: notifTab === k ? "2px solid #D4620D" : "2px solid transparent",
                marginBottom: -1,
              }}>{label}</button>
            ))}
          </div>

          {/* ── Tab: Enviar ── */}
          {notifTab === "send" && (
            <>
              <div style={s.modalBody}>
                <label style={s.modalLabel}>Destinatario</label>
                <select style={s.modalSelect} value={notifTarget} onChange={(e) => setNotifTarget(e.target.value)}>
                  <option value="all">Todos los usuarios</option>
                  {employees.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
                </select>
                <label style={{ ...s.modalLabel, marginTop: 14 }}>Ícono</label>
                <div style={s.emojiGrid}>
                  {ICONS.map(({ name, Icon, label }) => (
                    <button key={name} style={{ ...s.emojiBtn, ...(notifIcon === name ? s.emojiBtnActive : {}) }}
                      onClick={() => setNotifIcon(notifIcon === name ? "" : name)} title={label}>
                      <Icon size={18} color={notifIcon === name ? "#D4620D" : "var(--text-2)"} />
                    </button>
                  ))}
                </div>
                <label style={{ ...s.modalLabel, marginTop: 14 }}>Título <span style={{ fontWeight: 400, textTransform: "none", letterSpacing: 0 }}>(opcional)</span></label>
                <input style={s.modalSelect} placeholder="Ej: PREMIO EN CURSO" value={notifTitle} onChange={(e) => setNotifTitle(e.target.value)} maxLength={80} />
                <label style={{ ...s.modalLabel, marginTop: 14 }}>Mensaje</label>
                <textarea style={s.modalTextarea} value={notifText} onChange={(e) => setNotifText(e.target.value)}
                  placeholder="Ej: Completando 250 medidores ganás $15.000" rows={3} maxLength={500} />
                <div style={s.modalChars}>{notifText.length}/500</div>
                {/* Guardar como plantilla */}
                {notifText.trim() && (
                  <div style={{ display: "flex", gap: 8, marginTop: 10, alignItems: "center" }}>
                    <input style={{ ...s.modalSelect, flex: 1 }} placeholder="Nombre de la plantilla (opcional)"
                      value={newTemplateName} onChange={(e) => setNewTemplateName(e.target.value)} />
                    <button style={{ ...s.modalSend, opacity: savingTemplate ? 0.6 : 1 }}
                      onClick={saveTemplate} disabled={!newTemplateName.trim() || savingTemplate}>
                      Guardar plantilla
                    </button>
                  </div>
                )}
              </div>
              <div style={s.modalFooter}>
                {notifSent ? <div style={s.modalSuccess}>✓ Aviso enviado</div> : <>
                  <button style={s.modalCancel} onClick={() => setNotifModal(false)}>Cancelar</button>
                  <button style={{ ...s.modalSend, ...(!notifText.trim() ? s.modalSendDisabled : {}) }}
                    onClick={sendNotification} disabled={!notifText.trim()}>
                    <Bell size={14} /> Enviar aviso
                  </button>
                </>}
              </div>
            </>
          )}

          {/* ── Tab: Historial ── */}
          {notifTab === "history" && (
            <div style={{ ...s.modalBody, maxHeight: 400, overflowY: "auto" }}>
              {notifHistory.length === 0 ? (
                <p style={{ color: "var(--text-2)", fontSize: 13, textAlign: "center", padding: "20px 0" }}>Sin historial de avisos</p>
              ) : notifHistory.map((h) => (
                <div key={h.id} style={{ padding: "12px 0", borderBottom: "1px solid var(--border)", display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {h.title && <div style={{ fontWeight: 700, fontSize: 13, color: "var(--text)", marginBottom: 2 }}>{h.title}</div>}
                    <div style={{ fontSize: 13, color: "var(--text-2)", marginBottom: 4 }}>{h.content}</div>
                    <div style={{ fontSize: 11, color: "var(--text-3)" }}>
                      {h.target_user_name ? `→ ${h.target_user_name}` : "→ Todos"} · {new Date(h.sent_at).toLocaleString("es-AR", { day:"2-digit", month:"2-digit", year:"numeric", hour:"2-digit", minute:"2-digit" })}
                    </div>
                  </div>
                  <button onClick={() => resendNotification(h)} style={{ ...s.modalSend, padding: "5px 12px", fontSize: 12, flexShrink: 0 }}>
                    Reenviar
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* ── Tab: Plantillas ── */}
          {notifTab === "templates" && (
            <div style={{ ...s.modalBody, maxHeight: 400, overflowY: "auto" }}>
              {notifTemplates.length === 0 ? (
                <p style={{ color: "var(--text-2)", fontSize: 13, textAlign: "center", padding: "20px 0" }}>Sin plantillas guardadas. Creá una desde la pestaña Enviar.</p>
              ) : notifTemplates.map((t) => (
                <div key={t.id} style={{ padding: "12px 0", borderBottom: "1px solid var(--border)", display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: 13, color: "var(--text)", marginBottom: 2 }}>{t.name}</div>
                    {t.title && <div style={{ fontSize: 12, color: "#D4620D", marginBottom: 2 }}>{t.title}</div>}
                    <div style={{ fontSize: 13, color: "var(--text-2)" }}>{t.content}</div>
                  </div>
                  <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                    <button onClick={() => applyTemplate(t)} style={{ ...s.modalSend, padding: "5px 12px", fontSize: 12 }}>Usar</button>
                    <button onClick={() => removeTemplate(t.id)} style={{ ...s.modalCancel, padding: "5px 12px", fontSize: 12, color: "#dc2626" }}>✕</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    )}
    </>
  );
}

// ── Config section (phone number) ─────────────────────────────────────────────

function ConfigSection({ user }) {
  const isAdminPlus = user?.role === "admin-plus";
  const [phone, setPhone]     = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [saved, setSaved]     = useState(false);
  const [error, setError]     = useState("");

  useEffect(() => {
    getSettings()
      .then((d) => setPhone(d.support_phone || ""))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  async function handleSave() {
    setSaving(true); setError(""); setSaved(false);
    try { await updateSetting("support_phone", phone.trim()); setSaved(true); }
    catch (err) { setError(err.message || "Error al guardar"); }
    finally { setSaving(false); }
  }

  return (
    <div style={s.configWrap}>
      {error  && <div style={s.errorBanner}>{error}</div>}
      {saved  && <div style={s.successBanner}>✓ Número guardado correctamente</div>}

      <div style={s.configCard}>
        <div style={s.cardHeader}>
          <Phone size={18} color="#D4620D" />
          <span style={s.cardTitle}>Número de soporte</span>
          {isAdminPlus && (
            <button style={{ ...s.btnSave, ...(saving || loading ? s.btnSaveDisabled : {}), marginLeft: "auto" }} onClick={handleSave} disabled={saving || loading}>
              <Save size={14} style={{ marginRight: 6 }} />{saving ? "Guardando..." : "Guardar"}
            </button>
          )}
        </div>
        <p style={s.hint}>Este número aparece en la sección <strong>Soporte</strong> de la app. Los operarios pueden llamar o escribir por WhatsApp directamente.</p>
        {loading
          ? <div style={{ color: "var(--text-2)", fontSize: 13 }}>Cargando...</div>
          : <div style={s.inputRow}>
              <div style={s.phoneIcon}><Phone size={16} color="#D4620D" /></div>
              <input style={s.phoneInput} value={phone} onChange={(e) => { setPhone(e.target.value); setSaved(false); }} placeholder="+5491112345678" disabled={!isAdminPlus} />
            </div>
        }
        {!isAdminPlus && <p style={s.readonlyNotice}>Solo los administradores Plus pueden modificar este número.</p>}
      </div>

      {phone && (
        <div style={s.preview}>
          <p style={s.previewTitle}>Vista previa en la app</p>
          <div style={s.previewCard}>
            <div style={s.previewPhone}>{phone}</div>
            <div style={s.previewBtns}>
              <div style={s.previewCallBtn}>📞 Llamar</div>
              <div style={s.previewWaBtn}>💬 WhatsApp</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────

const s = {
  page:        { width: "100%", display: "flex", flexDirection: "column", height: "calc(100vh - 72px)" },
  pageHeader:  { marginBottom: 16, flexShrink: 0 },
  pageTitle:   { fontSize: 22, fontWeight: 800, color: "var(--text)", margin: 0, marginBottom: 2 },
  pageSubtitle:{ fontSize: 13, color: "var(--text-2)", margin: 0 },

  mainTabs:    { display: "flex", gap: 4, marginBottom: 16, flexShrink: 0 },
  mainTab:     { display: "flex", alignItems: "center", gap: 7, padding: "9px 20px", borderRadius: 10, border: "1px solid var(--border)", background: "var(--bg-card)", color: "var(--text-2)", fontWeight: 700, fontSize: 13, cursor: "pointer", fontFamily: "Montserrat, sans-serif", transition: "all .15s" },
  mainTabActive: { background: "#D4620D", color: "#fff", borderColor: "#D4620D" },

  // Chat
  chatWrap:    { flex: 1, display: "flex", flexDirection: "column", minHeight: 0 },
  chatSubHeader: { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12, flexShrink: 0 },
  connStatus:  { display: "flex", alignItems: "center", gap: 8 },
  connDot:     { width: 9, height: 9, borderRadius: "50%" },
  connText:    { fontSize: 12, color: "var(--text-2)", fontWeight: 600 },
  unreadBadge: { background: "#D4620D", color: "#fff", borderRadius: 10, padding: "2px 9px", fontSize: 11, fontWeight: 700 },
  reconnectBtn: { padding: "3px 10px", borderRadius: 8, border: "1px solid #d1d5db", background: "var(--bg-card)", color: "var(--text-2)", fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "Montserrat, sans-serif" },
  notifBtn:    { display: "flex", alignItems: "center", gap: 7, padding: "8px 16px", borderRadius: 10, background: "#FBF0E8", border: "1px solid #f4c8a0", color: "#D4620D", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "Montserrat, sans-serif" },

  layout:      { display: "flex", flex: 1, borderRadius: 16, overflow: "hidden", border: "1px solid var(--border)", minHeight: 0 },
  sidebar:     { width: 280, flexShrink: 0, background: "var(--bg-card)", borderRight: "1px solid var(--border)", display: "flex", flexDirection: "column", overflowY: "auto" },
  sidebarEmpty:{ display: "flex", flexDirection: "column", alignItems: "center", padding: "40px 16px" },
  tabs:        { display: "flex", borderBottom: "1px solid var(--border)", flexShrink: 0 },
  tabBtn:      { flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "11px 8px", border: "none", background: "none", cursor: "pointer", fontSize: 12, fontWeight: 700, color: "var(--text-3)", fontFamily: "inherit", borderBottom: "2px solid transparent" },
  tabBtnActive:{ color: "#D4620D", borderBottom: "2px solid #D4620D" },
  convItem:    { display: "flex", alignItems: "center", gap: 12, padding: "12px 16px", background: "none", border: "none", borderBottom: "1px solid var(--border)", cursor: "pointer", textAlign: "left", width: "100%", fontFamily: "Montserrat, sans-serif" },
  convItemActive: { background: "var(--primary-bg)" },
  convAvatar:  { width: 38, height: 38, borderRadius: "50%", background: "#FBF0E8", color: "#D4620D", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 800, flexShrink: 0 },
  convInfo:    { flex: 1, minWidth: 0 },
  convName:    { fontSize: 13, fontWeight: 700, color: "var(--text)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  convLast:    { fontSize: 11, color: "var(--text-3)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", marginTop: 2 },
  convBadge:   { background: "#D4620D", color: "#fff", borderRadius: 100, fontSize: 11, fontWeight: 700, padding: "2px 7px", flexShrink: 0 },
  chatPanel:   { flex: 1, display: "flex", flexDirection: "column", background: "#f7f7f5", minWidth: 0 },
  noSelection: { flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" },
  noSelectionText: { fontSize: 14, color: "var(--text-3)", marginTop: 12 },
  chatHeader:  { display: "flex", alignItems: "center", gap: 12, padding: "14px 20px", background: "var(--bg-card)", borderBottom: "1px solid var(--border)", flexShrink: 0 },
  chatAvatar:  { width: 38, height: 38, borderRadius: "50%", background: "#FBF0E8", color: "#D4620D", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 800 },
  chatHeaderName:  { fontSize: 14, fontWeight: 700, color: "var(--text)" },
  chatHeaderEmail: { fontSize: 11, color: "var(--text-3)" },
  chatHeaderPhone: {
    display: "flex", alignItems: "center", gap: 7,
    padding: "8px 16px", borderRadius: 10,
    background: "#FBF0E8", border: "1px solid #f4c8a0",
    color: "#D4620D", fontSize: 13, fontWeight: 700,
    textDecoration: "none", whiteSpace: "nowrap",
    cursor: "pointer", fontFamily: "Montserrat, sans-serif",
  },
  closeChatBtn: { padding: "7px 14px", borderRadius: 8, border: "none", background: "#fee2e2", color: "#dc2626", fontWeight: 700, fontSize: 12, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap" },
  ticketClosedBadge:    { display: "flex", alignItems: "center", gap: 5, background: "#dcfce7", color: "#16a34a", borderRadius: 8, padding: "4px 10px", fontSize: 12, fontWeight: 700 },
  ticketArchivedBadge:  { display: "flex", alignItems: "center", gap: 5, background: "#f0fdf4", color: "#15803d", borderRadius: 8, padding: "4px 10px", fontSize: 12, fontWeight: 700 },
  archiveBtn: { padding: "6px 12px", borderRadius: 8, border: "1px solid #d1d5db", background: "var(--bg-subtle)", color: "var(--text-2)", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "Montserrat, sans-serif", whiteSpace: "nowrap" },
  archivedToggleBtn: { display: "flex", alignItems: "center", justifyContent: "center", width: "100%", padding: "10px", background: "none", border: "none", borderTop: "1px solid var(--border)", color: "var(--text-3)", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "Montserrat, sans-serif", marginTop: "auto" },
  ticketBanner: { background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 10, padding: "10px 14px", fontSize: 12, color: "#64748b", fontWeight: 600, marginBottom: 12, textAlign: "center" },
  messages:    { flex: 1, overflowY: "auto", padding: "16px 20px", display: "flex", flexDirection: "column", gap: 4 },
  messagesEmpty: { flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", paddingTop: 40 },
  dayDivider:  { display: "flex", justifyContent: "center", margin: "12px 0" },
  dayLabel:    { fontSize: 11, color: "var(--text-3)", background: "var(--bg-subtle)", borderRadius: 10, padding: "3px 12px" },
  msgRow:      { display: "flex", alignItems: "flex-end", gap: 8, marginBottom: 2 },
  userAvatar:  { width: 28, height: 28, borderRadius: "50%", background: "#FBF0E8", color: "#D4620D", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 800, flexShrink: 0 },
  bubble:      { maxWidth: "65%", borderRadius: 18, padding: "10px 14px" },
  bubbleUser:  { background: "#fff", borderBottomLeftRadius: 4, boxShadow: "0 1px 3px rgba(0,0,0,0.06)", color: "#1a1a18" },
  bubbleAdmin: { background: "#D4620D", borderBottomRightRadius: 4, color: "#fff" },
  bubbleText:  { margin: 0, fontSize: 13, lineHeight: 1.5, whiteSpace: "pre-wrap", wordBreak: "break-word" },
  bubbleTime:  { display: "block", fontSize: 10, marginTop: 4, opacity: 0.65, textAlign: "right" },
  inputRow:    { display: "flex", alignItems: "flex-end", gap: 10, padding: "12px 16px", background: "var(--bg-card)", borderTop: "1px solid var(--border)", flexShrink: 0 },
  input:       { flex: 1, resize: "none", border: "1px solid var(--border)", borderRadius: 12, padding: "10px 14px", fontSize: 13, fontFamily: "Montserrat, sans-serif", color: "var(--text)", background: "var(--bg)", outline: "none", maxHeight: 120, lineHeight: 1.5 },
  sendBtn:     { display: "flex", alignItems: "center", justifyContent: "center", width: 42, height: 42, borderRadius: "50%", background: "#D4620D", border: "none", color: "#fff", cursor: "pointer", flexShrink: 0 },
  sendBtnDisabled: { opacity: 0.4, cursor: "not-allowed" },

  attachBtn: { display: "flex", alignItems: "center", justifyContent: "center", width: 38, height: 38, borderRadius: "50%", border: "1px solid var(--border)", background: "var(--bg-card)", color: "var(--text-2)", cursor: "pointer", flexShrink: 0 },
  attachMenu: { position: "absolute", bottom: 48, left: 0, background: "var(--bg-card)", border: "1px solid var(--border)", borderRadius: 12, overflow: "hidden", boxShadow: "0 4px 16px rgba(0,0,0,0.12)", zIndex: 100, minWidth: 130 },
  attachMenuItem: { display: "flex", alignItems: "center", gap: 10, width: "100%", padding: "10px 14px", border: "none", background: "none", cursor: "pointer", fontSize: 13, fontWeight: 600, color: "var(--text)", fontFamily: "Montserrat, sans-serif", textAlign: "left" },

  mediaBubbleImg:   { maxWidth: 220, maxHeight: 200, borderRadius: 10, display: "block", cursor: "zoom-in", marginBottom: 4, objectFit: "cover" },
  mediaBubbleAudio: { display: "block", width: 220, marginBottom: 4, accentColor: "#D4620D" },
  mediaBubbleVideo: { display: "block", maxWidth: 260, maxHeight: 180, borderRadius: 10, marginBottom: 4 },

  lightboxOverlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.88)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999, cursor: "zoom-out" },
  lightboxImg:     { maxWidth: "90vw", maxHeight: "90vh", borderRadius: 10, objectFit: "contain" },
  lightboxClose:   { position: "absolute", top: 20, right: 24, background: "rgba(255,255,255,0.15)", border: "none", color: "#fff", fontSize: 20, fontWeight: 700, cursor: "pointer", borderRadius: 10, width: 36, height: 36, display: "flex", alignItems: "center", justifyContent: "center" },

  // Modal
  modalOverlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9000 },
  modal:        { background: "var(--bg-card)", borderRadius: 18, width: "100%", maxWidth: 480, boxShadow: "0 8px 40px rgba(0,0,0,0.18)", overflow: "hidden" },
  modalHeader:  { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "18px 20px", borderBottom: "1px solid var(--border)" },
  modalIcon:    { width: 36, height: 36, borderRadius: 10, background: "#FBF0E8", display: "flex", alignItems: "center", justifyContent: "center" },
  modalTitle:   { fontSize: 16, fontWeight: 800, color: "var(--text)" },
  modalClose:   { background: "none", border: "none", color: "var(--text-2)", cursor: "pointer", padding: 4 },
  modalBody:    { padding: "20px 20px 8px" },
  modalLabel:   { display: "block", fontSize: 12, fontWeight: 700, color: "var(--text-2)", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.4px" },
  modalSelect:  { width: "100%", padding: "9px 12px", borderRadius: 10, border: "1px solid var(--border)", background: "var(--bg)", color: "var(--text)", fontSize: 13, fontFamily: "Montserrat, sans-serif", outline: "none" },
  modalTextarea:{ width: "100%", padding: "10px 12px", borderRadius: 10, border: "1px solid var(--border)", background: "var(--bg)", color: "var(--text)", fontSize: 13, fontFamily: "Montserrat, sans-serif", resize: "none", outline: "none", lineHeight: 1.5, boxSizing: "border-box" },
  modalChars:   { fontSize: 11, color: "var(--text-3)", textAlign: "right", marginTop: 4 },
  modalFooter:  { display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 10, padding: "16px 20px", borderTop: "1px solid var(--border)" },
  modalCancel:  { padding: "9px 18px", borderRadius: 10, border: "1px solid var(--border)", background: "var(--bg-subtle)", color: "var(--text-2)", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "Montserrat, sans-serif" },
  modalSend:    { display: "flex", alignItems: "center", gap: 7, padding: "9px 20px", borderRadius: 10, background: "#D4620D", border: "none", color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "Montserrat, sans-serif" },
  modalSendDisabled: { opacity: 0.4, cursor: "not-allowed" },
  modalSuccess: { color: "#16a34a", fontWeight: 700, fontSize: 14, padding: "8px 0" },

  emojiGrid: { display: "flex", flexWrap: "wrap", gap: 6, marginTop: 4 },
  emojiBtn: {
    width: 38, height: 38, borderRadius: 8, border: "1px solid var(--border)",
    background: "var(--bg-subtle)", fontSize: 20, cursor: "pointer",
    display: "flex", alignItems: "center", justifyContent: "center",
    transition: "all .12s",
  },
  emojiBtnActive: { border: "2px solid #D4620D", background: "#FBF0E8", transform: "scale(1.15)" },

  notifPreview: { marginTop: 16 },
  notifPreviewLabel: { fontSize: 11, fontWeight: 700, color: "var(--text-3)", textTransform: "uppercase", letterSpacing: "0.4px", marginBottom: 6 },
  notifPreviewCard: {
    background: "var(--bg-subtle)", border: "1px solid var(--border)",
    borderRadius: 12, padding: "12px 14px",
  },
  notifPreviewHeader: { display: "flex", alignItems: "center", gap: 8, marginBottom: 6 },
  notifPreviewIconWrap: {
    width: 30, height: 30, borderRadius: 8, background: "#FBF0E8",
    display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
  },
  notifPreviewTitle: { fontSize: 14, fontWeight: 800, color: "var(--text)" },
  notifPreviewBody:  { fontSize: 13, color: "var(--text-2)", lineHeight: 1.5, whiteSpace: "pre-wrap", marginTop: 4 },

  // Config
  configWrap:  { flex: 1, overflowY: "auto", paddingBottom: 40 },
  configCard:  { background: "var(--card)", borderRadius: 16, padding: 24, border: "1px solid var(--border)", marginBottom: 24, maxWidth: 520 },
  cardHeader:  { display: "flex", alignItems: "center", gap: 10, marginBottom: 12 },
  cardTitle:   { fontSize: 15, fontWeight: 700, color: "var(--text)" },
  hint:        { fontSize: 13, color: "var(--text-2)", marginBottom: 20, lineHeight: 1.6 },
  inputRow:    { display: "flex", alignItems: "center", gap: 10 },
  phoneIcon:   { width: 38, height: 38, borderRadius: 10, background: "var(--primary-bg)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  phoneInput:  { flex: 1, padding: "10px 14px", borderRadius: 10, border: "1px solid var(--border)", fontSize: 15, fontWeight: 600, color: "var(--text)", background: "var(--bg-card)", fontFamily: "inherit", outline: "none" },
  readonlyNotice: { marginTop: 16, color: "var(--text-2)", fontSize: 12, fontStyle: "italic" },
  btnSave:     { display: "inline-flex", alignItems: "center", padding: "8px 16px", borderRadius: 10, border: "none", background: "#D4620D", color: "#fff", fontWeight: 700, cursor: "pointer", fontSize: 13, fontFamily: "Montserrat, sans-serif" },
  btnSaveDisabled: { opacity: 0.5, cursor: "not-allowed" },
  errorBanner: { background: "#fef2f2", border: "1px solid #fecaca", color: "#dc2626", borderRadius: 10, padding: "10px 16px", marginBottom: 16, fontSize: 13 },
  successBanner: { background: "#f0fdf4", border: "1px solid #bbf7d0", color: "#16a34a", borderRadius: 10, padding: "10px 16px", marginBottom: 16, fontSize: 13, fontWeight: 600 },
  preview:     { maxWidth: 520 },
  previewTitle:{ fontSize: 11, fontWeight: 700, color: "var(--text-2)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 10 },
  previewCard: { background: "var(--card)", border: "1px solid var(--border)", borderRadius: 16, padding: 20, display: "flex", flexDirection: "column", alignItems: "center", gap: 16 },
  previewPhone:{ fontSize: 20, fontWeight: 800, color: "var(--text)", letterSpacing: 1 },
  previewBtns: { display: "flex", gap: 12, width: "100%" },
  previewCallBtn: { flex: 1, padding: "12px", borderRadius: 12, background: "#D4620D", color: "#fff", fontWeight: 700, fontSize: 14, textAlign: "center" },
  previewWaBtn:{ flex: 1, padding: "12px", borderRadius: 12, border: "2px solid #86efac", background: "#f0fdf4", color: "#16a34a", fontWeight: 700, fontSize: 14, textAlign: "center" },
};
